/**
 * Typed client for open-meteo's multi-model forecast endpoint.
 *
 * meteocompare has no backend of its own: it is a frontend that reads
 * open-meteo directly, which is free, unauthenticated and CORS-open. This
 * talks to the same endpoint with the same shape, so the comparison here is
 * the comparison meteocompare makes.
 *
 * Docs: https://open-meteo.com/en/docs
 */
import { MODEL_IDS } from "./models";
import { tracked } from "../progress";
import { timedFetch } from "../timedFetch";

const FORECAST_URL = "https://api.open-meteo.com/v1/forecast";

/** One call to the forecast endpoint, read to the end. */
async function ask<T>(params: URLSearchParams, signal?: AbortSignal): Promise<T> {
  const response = await timedFetch(`${FORECAST_URL}?${params}`, { signal });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`open-meteo ${response.status}: ${body || response.statusText}`);
  }
  return (await response.json()) as T;
}

/** open-meteo suffixes every per-model column with the model id. */
const DAILY_VARS = [
  "weather_code",
  "temperature_2m_max",
  "temperature_2m_min",
  "precipitation_sum",
  "precipitation_probability_max",
  "wind_speed_10m_max",
] as const;

export type DailyVar = (typeof DAILY_VARS)[number];

export interface ForecastResponse {
  latitude: number;
  longitude: number;
  elevation: number;
  utc_offset_seconds: number;
  timezone: string;
  daily: { time: string[] } & Partial<Record<string, (number | null)[]>>;
  daily_units: Record<string, string>;
}

export interface ForecastRequest {
  lat: number;
  lon: number;
  models?: string[];
  forecastDays?: number;
  signal?: AbortSignal;
}

/**
 * One variable's values for one day, per model, with the models that had
 * nothing to say left out entirely rather than carried as nulls.
 */
export type PerModel = Record<string, number>;

export interface DailyForecast {
  /** ISO date, local to the queried point. */
  date: string;
  values: Record<DailyVar, PerModel>;
}

export interface Forecast {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  units: Record<string, string>;
  days: DailyForecast[];
  /** Models that returned at least one value; the rest do not cover this point. */
  respondingModels: string[];
}

export async function fetchForecast(req: ForecastRequest): Promise<Forecast> {
  const models = req.models ?? MODEL_IDS;
  const params = new URLSearchParams({
    latitude: String(req.lat),
    longitude: String(req.lon),
    daily: DAILY_VARS.join(","),
    models: models.join(","),
    forecast_days: String(req.forecastDays ?? 7),
    timezone: "auto",
    wind_speed_unit: "kmh",
    precipitation_unit: "mm",
    temperature_unit: "celsius",
  });

  const raw = await tracked("open-meteo", () => ask<ForecastResponse>(params, req.signal));
  return shape(raw, models);
}

/** Turn open-meteo's `<var>_<model>` columns into per-day, per-model values. */
function shape(raw: ForecastResponse, models: string[]): Forecast {
  const responding = new Set<string>();

  const days: DailyForecast[] = raw.daily.time.map((date, index) => {
    const values = {} as Record<DailyVar, PerModel>;
    for (const variable of DAILY_VARS) {
      const perModel: PerModel = {};
      for (const id of models) {
        const column = raw.daily[`${variable}_${id}`];
        const value = column?.[index];
        // A model that does not cover this point returns nulls throughout;
        // dropping it here keeps it out of the consensus and out of the
        // effective model count.
        if (typeof value === "number" && Number.isFinite(value)) {
          perModel[id] = value;
          responding.add(id);
        }
      }
      values[variable] = perModel;
    }
    return { date, values };
  });

  return {
    latitude: raw.latitude,
    longitude: raw.longitude,
    elevation: raw.elevation,
    timezone: raw.timezone,
    units: raw.daily_units,
    days,
    respondingModels: models.filter((id) => responding.has(id)),
  };
}

/** One hourly variable, per model, over the forecast horizon. */
export interface HourlySeries {
  /** Epoch milliseconds, one per step. */
  times: number[];
  /** Model id -> its value at each step; null where that model has nothing. */
  series: Record<string, Array<number | null>>;
  units: string;
}

/**
 * Hourly values for every model at once, for plotting them against each other.
 *
 * The daily fetch above answers "what will the day be like"; this answers "how
 * far apart are the models, and when", which only reads as a curve over time.
 * Same endpoint, `hourly` instead of `daily`.
 */
/**
 * Answers already had, for ten minutes: the strip on the map and the full
 * comparison it opens ask for the same series at the same point, and
 * open-meteo's free tier is rate limited. Keyed to about 100 m, which is
 * finer than any model's grid. A failed fetch is dropped, so it is retried.
 */
const HOURLY_TTL_MS = 10 * 60 * 1000;
const hourlyCache = new Map<string, { at: number; answer: Promise<HourlySeries> }>();

export function fetchHourlySeries(
  req: ForecastRequest & { variable?: string },
): Promise<HourlySeries> {
  // A caller that can abort gets its own request: one reader giving up must
  // not cancel the answer another is waiting on.
  if (req.signal) return fetchHourlySeriesUncached(req);
  const key = [
    req.lat.toFixed(3), req.lon.toFixed(3), req.variable ?? "temperature_2m",
    req.forecastDays ?? 7, (req.models ?? MODEL_IDS).join(","),
  ].join("|");
  const cached = hourlyCache.get(key);
  if (cached && Date.now() - cached.at < HOURLY_TTL_MS) return cached.answer;
  const answer = fetchHourlySeriesUncached(req);
  hourlyCache.set(key, { at: Date.now(), answer });
  answer.catch(() => { if (hourlyCache.get(key)?.answer === answer) hourlyCache.delete(key); });
  return answer;
}

async function fetchHourlySeriesUncached(
  req: ForecastRequest & { variable?: string },
): Promise<HourlySeries> {
  const models = req.models ?? MODEL_IDS;
  const variable = req.variable ?? "temperature_2m";
  const params = new URLSearchParams({
    latitude: String(req.lat),
    longitude: String(req.lon),
    hourly: variable,
    models: models.join(","),
    forecast_days: String(req.forecastDays ?? 7),
    timezone: "auto",
    precipitation_unit: "mm",
    temperature_unit: "celsius",
  });

  const raw = await tracked("open-meteo", () => ask<{
    hourly: { time: string[] } & Partial<Record<string, Array<number | null>>>;
    hourly_units: Record<string, string>;
  }>(params, req.signal));

  const series: Record<string, Array<number | null>> = {};
  for (const id of models) {
    const column = raw.hourly[`${variable}_${id}`];
    // A model with no values here does not cover this point; leaving it out
    // keeps it out of the spread rather than flattening the band to zero.
    if (column?.some((v) => typeof v === "number" && Number.isFinite(v))) {
      series[id] = column;
    }
  }

  return {
    times: raw.hourly.time.map((t) => new Date(t).getTime()),
    series,
    units: raw.hourly_units[`${variable}_${models[0]}`] ?? "°C",
  };
}
